/**
 * Entity Extraction — matches event text against the entity registry
 * to identify companies, indices, sectors, commodities, crypto, and countries.
 *
 * Multi-stage extraction:
 * 1. Primary: title scan at full confidence
 * 2. Secondary: first 5 description matches at 0.9x confidence
 * 3. Related: entities linked via relatedEntities at 0.8x confidence
 *
 * Match confidence: exact name (95%), alias (90%), keyword (70%).
 * Deduplicates by entity ID, keeping highest confidence match.
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { ExtractedEntityMatch } from './types';
import { ENTITY_REGISTRY, type EntityDef } from '@/config/entities';

// ---------------------------------------------------------------------------
// Compiled pattern cache (lazy init)
// ---------------------------------------------------------------------------

interface CompiledPattern {
  regex: RegExp;
  entityDef: EntityDef;
  matchType: 'name' | 'alias' | 'keyword';
  baseConfidence: number;
}

let _patterns: CompiledPattern[] | null = null;

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildPatterns(): CompiledPattern[] {
  if (_patterns) return _patterns;

  const results: CompiledPattern[] = [];

  for (const entity of ENTITY_REGISTRY) {
    // Primary name match (95% confidence)
    if (entity.name.length >= 3) {
      try {
        results.push({
          regex: new RegExp(`\\b${escapeRegex(entity.name)}\\b`, 'i'),
          entityDef: entity,
          matchType: 'name',
          baseConfidence: 0.95,
        });
      } catch { /* skip invalid regex */ }
    }

    // Alias matches (90% confidence)
    for (const alias of entity.aliases) {
      if (alias.length < 2) continue;
      try {
        results.push({
          regex: new RegExp(`\\b${escapeRegex(alias)}\\b`, 'i'),
          entityDef: entity,
          matchType: 'alias',
          baseConfidence: 0.90,
        });
      } catch { /* skip */ }
    }

    // Keyword matches (70% confidence)
    for (const kw of entity.keywords) {
      if (kw.length < 4) continue; // Skip very short keywords to avoid false positives
      try {
        results.push({
          regex: new RegExp(`\\b${escapeRegex(kw)}\\b`, 'i'),
          entityDef: entity,
          matchType: 'keyword',
          baseConfidence: 0.70,
        });
      } catch { /* skip */ }
    }
  }

  // Sort longest pattern first for better match quality
  results.sort((a, b) => {
    const aLen = a.regex.source.length;
    const bLen = b.regex.source.length;
    return bLen - aLen;
  });

  _patterns = results;
  return results;
}

// ---------------------------------------------------------------------------
// Entity ID → EntityDef map (lazy init)
// ---------------------------------------------------------------------------

let _entityById: Map<string, EntityDef> | null = null;

function getEntityById(id: string): EntityDef | undefined {
  if (!_entityById) {
    _entityById = new Map();
    for (const e of ENTITY_REGISTRY) {
      _entityById.set(e.id, e);
    }
  }
  return _entityById.get(id);
}

// ---------------------------------------------------------------------------
// Extraction
// ---------------------------------------------------------------------------

/**
 * Scan text for entity matches using compiled patterns.
 * Returns matches deduped by entity ID (highest confidence kept).
 */
function scanText(
  text: string,
  source: ExtractedEntityMatch['source'],
  confidenceMultiplier: number,
  maxMatches: number,
): ExtractedEntityMatch[] {
  const patterns = buildPatterns();
  const seen = new Map<string, ExtractedEntityMatch>();
  let matchCount = 0;

  for (const p of patterns) {
    if (matchCount >= maxMatches) break;

    if (p.regex.test(text)) {
      const id = p.entityDef.id;
      const confidence = p.baseConfidence * confidenceMultiplier;

      const existing = seen.get(id);
      if (!existing || existing.confidence < confidence) {
        seen.set(id, {
          entityId: id,
          name: p.entityDef.name,
          type: p.entityDef.type,
          confidence: Math.round(confidence * 100) / 100,
          matchType: p.matchType,
          source,
        });

        if (!existing) matchCount++;
      }
    }
  }

  return Array.from(seen.values());
}

/**
 * Extract entities from an event using multi-stage scanning.
 *
 * Stage 1: Title scan (full confidence) — all matches
 * Stage 2: Description scan (0.9x confidence) — max 5 matches
 * Stage 3: Related entities (0.8x confidence) — from entities found in stages 1-2
 *
 * Returns deduplicated matches sorted by confidence descending.
 */
export function extractEntities(event: NormalizedEvent): ExtractedEntityMatch[] {
  const matches = new Map<string, ExtractedEntityMatch>();

  // Stage 1: Title scan — full confidence, unlimited matches
  const titleMatches = scanText(event.title, 'title', 1.0, 50);
  for (const m of titleMatches) {
    matches.set(m.entityId, m);
  }

  // Stage 2: Description scan — 0.9x confidence, max 5 new matches
  if (event.description) {
    const descMatches = scanText(event.description, 'description', 0.9, 20);
    let added = 0;
    for (const m of descMatches) {
      if (added >= 5) break;
      const existing = matches.get(m.entityId);
      if (!existing) {
        matches.set(m.entityId, m);
        added++;
      } else if (existing.confidence < m.confidence) {
        matches.set(m.entityId, m);
      }
    }
  }

  // Stage 3: Related entities — 0.8x confidence
  const relatedIds = new Set<string>();
  for (const [, match] of matches) {
    const entity = getEntityById(match.entityId);
    if (entity?.relatedEntities) {
      for (const relId of entity.relatedEntities) {
        if (!matches.has(relId)) {
          relatedIds.add(relId);
        }
      }
    }
  }

  for (const relId of relatedIds) {
    const relEntity = getEntityById(relId);
    if (relEntity) {
      matches.set(relId, {
        entityId: relId,
        name: relEntity.name,
        type: relEntity.type,
        confidence: 0.80 * 0.70, // Related × keyword-level base
        matchType: 'keyword',
        source: 'related',
      });
    }
  }

  // Sort by confidence descending
  const result = Array.from(matches.values());
  result.sort((a, b) => b.confidence - a.confidence);

  return result;
}
